import type { EntityAssociateType } from "@/schemas/digital-files";
import type { RegistryType } from "@/schemas/digital-files/registry";
import type { UserType } from "@/types/entities/users";
import { OWNER_ROLE_ID, type EntityModalState } from "../types";

export const getDocumentType = (
  documentType: string | undefined | null,
): string => {
  if (!documentType) return "dui";
  const docType = documentType.toLowerCase();
  if (docType === "auto") return "dui";
  return docType;
};

export const getEntityOperationModal = (
  operation: EntityModalState,
  version: number,
  entityName?: string,
) => {
  if (operation === "deactivate") {
    return {
      title: "¿Estás seguro que quieres desactivar?",
      description: "Una vez desactivada, los registros no estarán disponibles.",
      acceptText: "Desactivar",
    };
  }
  if (operation === "reactivate") {
    return {
      title: "¿Estás seguro que quieres reactivar?",
      description: "Una vez activada, los registros estarán disponibles.",
      acceptText: "Reactivar",
    };
  }
  if (operation === "activate" && version > 1) {
    return {
      title: "Activar nueva versión",
      description: `Al activar la nueva versión ${entityName ? "de " + entityName : ""}, los trámites que hacen uso de este expediente deberán ser revisados para verificar que sus configuraciones siguen válidas`,
      acceptText: "Actualizar",
      modalContentClassName: "max-w-[720px]",
      acceptClassName:
        "border-surface-warning-solid bg-surface-warning-solid text-on-solid hover:border-surface-warning-solid-hover hover:bg-surface-warning-solid-hover focus:bg-surface-warning-solid-click disabled:bg-neutral-object-accent disabled:text-on-disabled disabled:border-transparent",
    };
  }
  return {
    title: "¿Estás seguro que quieres activar?",
    description: "Una vez activada, estará disponible para crear registros.",
    acceptText: "Activar",
  };
};

export const getAssociate = (user: UserType): EntityAssociateType => {
  return {
    applicant_id: user.id,
    applicant: {
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
    },
    role: null,
  };
};

/**
 * Format the associates data
 *
 * @param associates
 * @returns
 */
export const getNormalizedAssociates = (
  associates?: RegistryType["associates"],
) => {
  if (!associates || associates.length === 0) {
    return [];
  }

  const toArray = <T>(
    items: T[] | T | undefined | null,
    item: T | undefined | null,
  ): T[] => {
    if (Array.isArray(items)) {
      return item ? [item] : [];
    }
    if (item) {
      return [item];
    }
    return [];
  };

  return associates.map((associate) => {
    // Build roles array from new plural fields or legacy singular fields
    const roles = toArray(associate.roles, associate.role);
    const roleIds = toArray(associate.role_ids, associate.role_id);

    // Inject owner role if is_owner flag is set and not already present
    if (associate.is_owner && !roleIds.includes(OWNER_ROLE_ID)) {
      roles.unshift({ id: OWNER_ROLE_ID, name: "Propietario" });
      roleIds.unshift(OWNER_ROLE_ID);
    }

    return {
      ...associate,
      roles,
      role_ids: roleIds,
      // Keep legacy singular fields for backward compat
      role_id: roleIds[0] ?? associate.role_id,
      role: roles[0] ?? associate.role,
    };
  });
};

/**
 * Get the role badge color as per the role id
 * @param role_id
 * @returns neutral for normal users and action for owners
 */
export const getRoleBadgeColor = (role_id: string): "neutral" | "action" => {
  if (role_id === OWNER_ROLE_ID) {
    return "action";
  }
  return "neutral";
};

/**
 * Get the role badge label as per the role id
 * @param role_id
 * @returns neutral for "normal" users and "Propietario" for owners
 */
export const getRoleBadgeLabel = (role_id: string): string => {
  if (role_id === OWNER_ROLE_ID) {
    return "Propietario";
  }
  return "Normal";
};

export const getRoleIcon = (role_id: string): string => {
  if (role_id === OWNER_ROLE_ID) {
    return "user-shield";
  }
  return "user";
};
